import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subscription, filter } from 'rxjs';
import { cloneDeep } from 'lodash';

import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { DistributionService } from '@Common/TargetingShared/services/distribution.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { IncludeExcludeKeys } from '@Common/Misc/models/include-exclude-model';
import { JournodexRegionsService } from '@Common/Journodex/services/journodex-regions.service';
import { JournodexSubjectsService } from '@Common/Journodex/services/journodex-subjects.service';
import { TargetingSearchFormService } from '@Common/TargetingShared/services/targeting-search-form.service';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';

import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';

import { TargetingSearch } from '@Discover/Targeting/models/targeting-search.model';
import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { TargetingSearchFullModel } from '@Discover/Targeting/models/targeting-search-full.model';

import { RecommendedRecipientService } from '../RecommendedRecipient/recommended-recipient-service';

@Component({
    selector: 'agpr-quick-edit-panel',
    templateUrl: './quick-edit-panel.component.html',
    styleUrls: ['./quick-edit-panel.component.scss'],
    standalone: false
})
export class QuickEditPanelComponent extends AbstractDialogComponent implements OnDestroy, OnInit {

    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() distributionId: number;

    id: string = 'rr_quick_edit_panel';
    loading = true;
    initialLoading = true;
    hideSlideOverlay: boolean = false;
    isQueryEmpty: boolean = true;
    isPreviewLoading: boolean = false;
    previewCount: number = 0;
    previewCountString: string = '';

    defaultSearchModel: TargetingSearch;
    searchModel: TargetingSearch;
    targetingSearchFull: TargetingSearchFullModel;
    /** An object to cache all IncludeExclude properties on TargetingSearch as booleans for the template */

    defaultContactSearchParams: DropdownOptionModel[] = [
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.contact.names', 'contactIdsNames'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.monitoringKeywords', 'monitoringKeywords'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.coveringSubjects', 'subjects'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.regions', 'geographies'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.jobTitles', 'jobTitles'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.jobRoles', 'jobRoles'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.languages', 'languages'),
    ];

    defaultOutletSearchParams: DropdownOptionModel[] = [
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.outlet.names', 'outletIdsNames'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.monitoringKeywords', 'outletMonitoringKeywords'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.coveringSubjects', 'outletSubjects'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.targetAudienceRegion', 'areasOfCoverage'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.mediaMarkets', 'mediaMarkets'),
    ];

    defaultRefinementSearchParams: DropdownOptionModel[] = [
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.mediaTypes', 'mediaTypes'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.productTypes', 'productTypes'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.marketSectors', 'marketSectors'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.coverageFocuses', 'coverageFocuses'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.productFrequencies', 'productFrequencies'),
        new DropdownOptionModel('Discover.targeting.searchFieldLabels.targetAudiences', 'targetAudiences'),
    ];

    contactSearchParams: DropdownOptionModel[] = cloneDeep(this.defaultContactSearchParams);
    outletSearchParams: DropdownOptionModel[] = cloneDeep(this.defaultOutletSearchParams);
    refinementSearchParams: DropdownOptionModel[] = cloneDeep(this.defaultRefinementSearchParams);

    searchParamsVisible: {
        subjects: boolean;
        monitoringKeywords: boolean;
        geographies: boolean;
        areasOfCoverage: boolean;
        mediaMarkets: boolean;
        jobRoles: boolean;
        outletIdsNames: boolean;
        outletSubjects: boolean;
        outletMonitoringKeywords: boolean;
        languages: boolean;
        jobTitles: boolean;
        contactIdsNames: boolean;
        mediaTypes: boolean;
        productTypes: boolean;
        marketSectors: boolean;
        coverageFocuses: boolean;
        productFrequencies: boolean;
        targetAudiences: boolean;
    } = {
        subjects: false,
        monitoringKeywords: false,
        geographies: false,
        areasOfCoverage: false,
        mediaMarkets: false,
        jobRoles: false,
        outletIdsNames: false,
        outletSubjects: false,
        outletMonitoringKeywords: false,
        languages: false,
        jobTitles: false,
        contactIdsNames: false,
        mediaTypes: false,
        productTypes: false,
        marketSectors: false,
        coverageFocuses: false,
        productFrequencies: false,
        targetAudiences: false,
    };

    searchIncludeExcludeNotEmpty = cloneDeep(this.searchParamsVisible);

    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        protected distributionService: DistributionService,
        public recommendedRecipientService: RecommendedRecipientService,
        public journodexRegionsService: JournodexRegionsService,
        public journodexSubjectsService: JournodexSubjectsService,
        public journodexEnumsService: JournodexEnumsService,
        private targetingSearchFormService: TargetingSearchFormService,
    ) {
        super(coreDialogService, 'RRQuickEditPanel');
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.recommendedRecipientService.getCachedSearch().pipe(filter(x => !!x)).subscribe((cachedSearch) => {
                this.searchModel = cachedSearch.clone();
                this.setDefaultVisibleState();
                this.getFullSearch();
                this.isQueryEmpty = this.searchModel.hasEmptyQuery();
            }),
            this.recommendedRecipientService.getQuickEditSearch().pipe(filter(x => !!x)).subscribe((quickEditSearch) => {
                if (quickEditSearch) {
                    this.defaultSearchModel = quickEditSearch.clone();
                    this.setDefaultVisibleState();
                }
            }),
            this.recommendedRecipientService.getQuickEditLoading().subscribe((loading) => {
                this.loading = loading;
            }),
            this.recommendedRecipientService.getQuickEditPreviewCount().subscribe((count) => {
                this.previewCount = count;
                if (!count && count !== 0) {
                    this.previewCountString = ' >10000';
                    this.previewCount = Infinity;
                    return;
                }
                this.previewCount = count;
                this.previewCountString = (count > 10000) ? '>10000' : '' + count + '' ;
            }),
            this.recommendedRecipientService.getQuickEditPreviewCountLoading().subscribe((loading) => {
                this.isPreviewLoading = loading;
            }),
        );
    }

    getFullSearch() {
        this.targetingSearchFormService.constructFullSearch(this.searchModel).subscribe((search) => {
            this.targetingSearchFull = new TargetingSearchFullModel(search);
        });
    }

    handleToggle(event, key) {
        this.getFullSearch();
        if (event) {
            this.searchParamsVisible[key] = true;
            this.checkVisibleParams(key as string);
        }
    }

    checkVisibleParams(key: string) {
        const index = this.contactSearchParams.findIndex(param => param.value === key);
        if (index > -1) {
            this.contactSearchParams.splice(index, 1);
        }
        const outletIndex = this.outletSearchParams.findIndex(param => param.value === key);
        if (outletIndex > -1) {
            this.outletSearchParams.splice(outletIndex, 1);
        }
        const refinementIndex = this.refinementSearchParams.findIndex(param => param.value === key);
        if (refinementIndex > -1) {
            this.refinementSearchParams.splice(refinementIndex, 1);
        }
    }

    setDefaultVisibleState() {
        this.contactSearchParams = cloneDeep(this.defaultContactSearchParams);
        this.outletSearchParams = cloneDeep(this.defaultOutletSearchParams);
        this.refinementSearchParams = cloneDeep(this.defaultRefinementSearchParams);

        if (this.defaultSearchModel) {
            for (const k in this.searchIncludeExcludeNotEmpty) {
                if (this.defaultSearchModel.hasOwnProperty(k)) {
                    const key = k as keyof IncludeExcludeKeys<TargetingSearch> || '';
                    this.searchIncludeExcludeNotEmpty[key] = !this.defaultSearchModel[key].isEmpty();
                }
            }
        }
        if (this.searchModel) {
            for (const k in this.searchParamsVisible) {
                if (this.searchModel.hasOwnProperty(k)) {
                    const key = k as keyof IncludeExcludeKeys<TargetingSearch> || '';
                    this.searchParamsVisible[key] = !this.searchModel[key].isEmpty();
                    if (this.searchParamsVisible[key]) {
                        this.checkVisibleParams(key as string);
                    }
                }
            }
        }
        this.searchParamsVisible = cloneDeep(this.searchParamsVisible);
    }

    setVisibleParams() {
        this.contactSearchParams = cloneDeep(this.defaultContactSearchParams);
        this.outletSearchParams = cloneDeep(this.defaultOutletSearchParams);
        this.refinementSearchParams = cloneDeep(this.defaultRefinementSearchParams);

        for (const k in this.searchParamsVisible) {
            if (this.searchParamsVisible[k]) {
                this.checkVisibleParams(k as string);
            }
        }
        this.searchParamsVisible = cloneDeep(this.searchParamsVisible);
    }


    checkAll() {
        this.searchModel = this.defaultSearchModel.clone();
        this.searchModel.groupByOutlet = true;
        this.getFullSearch();
        this.setDefaultVisibleState();
    }

    uncheckAll() {
        this.searchModel = new TargetingSearch();
        this.searchModel.groupByOutlet = true;
        this.getFullSearch();
        this.setDefaultVisibleState();
    }

    apply() {
        this.close(this.searchModel);
    }

    close(searchModel?: TargetingSearch): void {
        this.ngOnDestroy();
        this.hideSlideOverlay = true;
        if (searchModel) {
            this.closeDialog(searchModel);
        } else {
            this.closeDialog();
        }
    }

    getQuickEditPreviewCount() {
        if (this.isQueryEmpty) {
            this.previewCountString = ' >10000';
            this.previewCount = Infinity;
        } else {
            this.recommendedRecipientService.fetchPreviewCount(this.searchModel);
        }

    }

    /**
     * Merges new changes into targetingSearch & update dirty flag
     */
    updateSearch(search: Partial<TargetingSearchFullModel>) {
        this.targetingSearchFull.merge(search);

        const _targetingSearch = this.searchModel.clone();
        _targetingSearch.merge(this.targetingSearchFull.toBaseSearch());
        this.searchModel = _targetingSearch;
        this.searchModel.groupByOutlet = true;
        this.searchModel = cloneDeep(this.searchModel);

        this.setVisibleParams();

        this.isQueryEmpty = this.searchModel.hasEmptyQuery();
        this.getQuickEditPreviewCount();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }
}
